import BigNumber from 'bignumber.js';
import {PRIMARY_CURRENCY_DECIMALS} from '~/config';
import {Currency, CurrencyFormatter} from '~/entities/currencies';
import {DashboardCategoryTotal} from '~/entities/dashboard-categories';
import {TransactionsHistory} from '~/entities/transactions-history';
import {AssetActionsRecord} from '~/features/dashboard-asset-pages';
import {MoneyFormat} from '~/library/money-kit';
import {Percent, Prose, Resource} from '~/library/types/src';
import {AccountDashboardCategory} from '~/processes/dashboard';
import {InstrumentDashboardCategory} from '~/processes/dashboard/model/InstrumentDashboardCategory';
import {selectProvidersResource} from '~/processes/dashboard/selectors/selectFiatProviders';
import {selectCurrencyServiceResource} from '~/redux/assets/assetsResource';
import {selectCurrentCurrency} from '~/redux/assets/assetsSelectors';
import {selectBalancesResource} from '~/redux/balances/balancesResource';
import {InstrumentType} from '~/redux/balances/balancesTypes';
import {selectFinancesResource} from '~/redux/finances';
import {AccountsOperationsAggregate, OperableAccount} from '~/redux/finances/domain';
import {getAllAccounts, getAllOperations} from '~/redux/finances/financesPrimitives';
import {goToOperation} from '~/redux/finances/financesRouting';
import {
    aggregateBalances,
    getInstrumentsResources,
    getMoneyResources,
} from '~/redux/finances/selectors/AccountTotalsView';

export const dashboardCategories = [
    AccountDashboardCategory.create(
        'fiat',
        selectAccountCategoryTotal(OperableAccount.isInCashGroup),
        selectAccounts(OperableAccount.isInCashGroup),
        selectProvidersResource
    ),
    AccountDashboardCategory.create(
        'crypto',
        selectAccountCategoryTotal(OperableAccount.isInCryptoGroup),
        selectAccounts(OperableAccount.isInCryptoGroup),
        selectProvidersResource
    ),
    InstrumentDashboardCategory.create('venture', selectZeroTotalResource, selectEmpty),
    InstrumentDashboardCategory.create('stocks', selectInstrumentCategoryTotal('stock'), selectInstruments('stock')),
    InstrumentDashboardCategory.create('funds', selectInstrumentCategoryTotal('etf'), selectInstruments('etf')),
    InstrumentDashboardCategory.create('bonds', selectInstrumentCategoryTotal('bond'), selectInstruments('bond')),
    InstrumentDashboardCategory.create('notes', selectInstrumentCategoryTotal('note'), selectInstruments('note')),
];

function selectInstruments(assetType: InstrumentType) {
    return (state: AppModel) =>
        selectBalancesResource(state).mapSuccess(balances =>
            balances
                .filter(b => b.type === assetType)
                .map((b): InstrumentDashboardCategory.Asset => {
                    const title = (() => {
                        switch (b.type) {
                            case 'etf':
                            case 'note':
                                return b.asset_name ?? 'NULL';
                            case 'bond':
                            case 'stock':
                                return b.issuer_name;
                        }
                    })();

                    return {
                        id: b.id,
                        icon: b.logo.vector_small || b.logo.raster_small || null,
                        ticker: b.ticker,
                        title: title,
                        issuer: Prose.make(b.issuer_name),
                        yield: b.coupon_rate && Prose.make(b.coupon_rate).pipe(Prose.ensureEndsWith('%')),
                        quantity: BigNumber(b.qty),
                        value: BigNumber(b.current_value),
                        currency: b.currency,
                    };
                })
        );
}

function getAccountHistoryFilter(account: OperableAccount): TransactionsHistory.Filters {
    switch (account.type) {
        case 'fiat':
            return {account: [account.id]};
        case 'crypto':
            return {wallet: [account.id]};
        case 'stablecoin':
            return {token: [account.id]};
    }
}

function getAccountActions(account: AccountsOperationsAggregate.AccountWithOperations): AssetActionsRecord {
    const {operations: op} = account;
    const actions: AssetActionsRecord = {};

    if (OperableAccount.isInCashGroup(account)) {
        actions.topup = op.topup?.[0] && goToOperation(op.topup[0]);
        actions.exchange = op.exchange?.[0] && goToOperation(op.exchange[0]);
        actions.withdraw = op.withdraw?.[0] && goToOperation(op.withdraw[0]);
    }

    if (OperableAccount.isInCryptoGroup(account)) {
        actions.receive = op.receive?.[0] && goToOperation(op.receive[0]);
        actions.buy = op.buy?.[0] && goToOperation(op.buy[0]);
        actions.sell = op.sell?.[0] && goToOperation(op.sell[0]);
        actions.send = op.send?.[0] && goToOperation(op.send[0]);
    }

    return actions;
}

function getAccountSummary(account: OperableAccount) {
    const formatter = CurrencyFormatter.make(account);
    const [...summary] = (function* () {
        if (parseFloat(account.balanceIn) > 0) {
            yield {label: 'Transfer in', value: formatter.format(account.balanceIn)};
        }
        if (parseFloat(account.balanceOut) > 0) {
            yield {label: 'Transfer out', value: formatter.format(account.balanceOut)};
        }
    })();
    return summary;
}

function selectAccounts(predicate: (account: OperableAccount) => boolean) {
    return (state: AppModel) =>
        Resource.all(
            selectFinancesResource(state).mapSuccess(finance =>
                Object.values(AccountsOperationsAggregate.fromLists(getAllOperations(finance), getAllAccounts(finance)))
                    .flat()
                    .filter(predicate)
                    .sort(compareCurrenycOrder)
            ),
            selectCurrencyServiceResource(state)
        ).mapSuccess(([accounts, cs]) =>
            accounts.map(
                (a): AccountDashboardCategory.Asset => ({
                    id: a.id,
                    icon: a.icon,
                    providerId: a.providerId,
                    title: a.currencyName,
                    currency: Currency.coerce(a),
                    balance: CurrencyFormatter.make(a).format(a.balance),
                    balanceInPrimary: cs.formatAsPrimary(a.balance, a.currencyCode),
                    actions: getAccountActions(a),
                    summary: getAccountSummary(a),
                    history: getAccountHistoryFilter(a),
                })
            )
        );
}

const SORT_ORDER: Record<string, number> = {
    EUR: 1100,
    USD: 1110,
    KZT: 1120,
    CHF: 1130,
    GBP: 1140,
    AED: 1150,
    RUB: 1160,

    BTC: 2000,
    ETH: 2100,
    USDC: 2200,
};

const SORT_ORDER_DEFAULT = 9999;

function compareCurrenycOrder(a: OperableAccount, b: OperableAccount): number {
    const orderA = SORT_ORDER[a.currencyCode ?? ''] ?? SORT_ORDER_DEFAULT;
    const orderB = SORT_ORDER[b.currencyCode ?? ''] ?? SORT_ORDER_DEFAULT;
    return orderA - orderB;
}

function selectAccountCategoryTotal(predicate: (account: OperableAccount) => boolean) {
    return (state: AppModel) =>
        getMoneyResources(state).mapSuccess(([finAccounts, cs, all]): DashboardCategoryTotal => {
            const accounts = finAccounts.filter(predicate);
            const {balance: total} = aggregateBalances(accounts, cs);
            return {
                count: accounts.length,
                percent: all.getPercentOf(accounts),
                balance: cs.formatAsPrimary(total),
            };
        });
}

function selectInstrumentCategoryTotal(instrumentType: InstrumentType) {
    return (state: AppModel) =>
        getInstrumentsResources(state).mapSuccess(([instruments, cs, all]): DashboardCategoryTotal => {
            const accounts = instruments.filter(balance => balance.type === instrumentType);
            const {balance: total} = aggregateBalances(accounts, cs);
            return {
                count: accounts.length,
                percent: all.getPercentOf(accounts),
                balance: cs.formatAsPrimary(total),
            };
        });
}

export const selectDemoTotal = selectZeroTotal;
function selectZeroTotalResource(state: AppModel) {
    return Resource.from({isSuccess: true, data: selectZeroTotal(state)});
}
function selectZeroTotal(state: AppModel): DashboardCategoryTotal {
    const primaryCurrency = selectCurrentCurrency(state);
    const formatter = MoneyFormat.from({code: primaryCurrency, decimals: PRIMARY_CURRENCY_DECIMALS});
    return {
        count: 0,
        percent: Percent.zero,
        balance: formatter.localeFormat(0),
    };
}

function selectEmpty(): Resource<InstrumentDashboardCategory.Asset[]> {
    return Resource.from({isSuccess: true, data: []});
}
